import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import ArtistsTab from './components/ArtistsTab';
import EventsTab from './components/EventsTab';
import NodesTab from './components/NodesTab';
import MarketTab from './components/MarketTab';
import FavoritesTab from './components/FavoritesTab';
import { BottomNav } from '../../components/feature/BottomNav';
import { useAuth } from '../../hooks/useAuth';
import { ArtistSearch } from '../directory/components/ArtistSearch';
import { PrefectureList } from '../directory/components/PrefectureList';
import { JapanMap } from '../directory/components/JapanMap';
import { japanPrefectures } from '../../mocks/japanPrefectures';
import { usePrefectureCounts } from '../../hooks/usePrefectureCounts';
import { RecommendSection } from './components/discover/RecommendSection';
import { HotSection } from './components/discover/HotSection';
import { PickupSection } from './components/discover/PickupSection';

type ExploreMode = 'discover' | 'search' | 'favorites';
type ActiveCategory = 'artists' | 'events' | 'nodes' | 'market';

// スクロール連動フェードインラッパー
function FadeInSection({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
      transition={{ duration: 0.75, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// 深淵オーブ背景
function AbyssOrbs() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      {/* メインオーブ（ティール） */}
      <motion.div
        className="absolute rounded-full blur-3xl"
        style={{
          width: '60vw',
          height: '60vw',
          background: 'radial-gradient(circle, rgba(34,211,238,0.6) 0%, transparent 65%)',
          opacity: 0.02,
          top: '-18vw',
          right: '-12vw',
        }}
        animate={{
          x: [0, -35, 22, -10, 0],
          y: [0, 28, -18, 12, 0],
          scale: [1, 1.12, 0.91, 1.06, 1],
        }}
        transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* サブオーブ（ローズ） */}
      <motion.div
        className="absolute rounded-full blur-3xl"
        style={{
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(251,113,133,0.6) 0%, transparent 65%)',
          opacity: 0.015,
          bottom: '0vh',
          left: '-10vw',
        }}
        animate={{
          x: [0, 32, -22, 8, 0],
          y: [0, -22, 32, -10, 0],
          scale: [1, 0.88, 1.14, 0.96, 1],
        }}
        transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut', delay: 6 }}
      />
    </div>
  );
}

export default function ExplorePage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [exploreMode, setExploreMode] = useState<ExploreMode>('discover');
  const [activeCategory, setActiveCategory] = useState<ActiveCategory>('artists');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [hoveredPref, setHoveredPref] = useState<string | null>(null);
  const { counts } = usePrefectureCounts();

  const modes = [
    { id: 'discover' as const, label: 'DISCOVER', icon: 'ri-compass-3-line' },
    { id: 'search' as const, label: 'SEARCH', icon: 'ri-search-line' },
    { id: 'favorites' as const, label: 'FAVORITES', icon: 'ri-heart-line' },
  ];

  const categories = [
    { id: 'artists' as const, label: 'MAKER', icon: 'ri-mic-line' },
    { id: 'nodes' as const, label: 'NODES', icon: 'ri-store-3-line' },
    { id: 'events' as const, label: 'EVENTS', icon: 'ri-calendar-event-line' },
    { id: 'market' as const, label: 'MARKET', icon: 'ri-shopping-bag-3-line' },
  ];

  const handlePrefectureSelect = (id: string) => {
    navigate(`/prefecture/${id}`);
  };

  // セクション見出しコンポーネント
  const SectionHeading = ({
    icon,
    iconColor,
    title,
    subtitle,
  }: {
    icon: string;
    iconColor: string;
    title: string;
    subtitle: string;
  }) => (
    <div className="flex items-center gap-3 mb-6">
      <motion.div
        className="w-10 h-10 flex items-center justify-center rounded-2xl flex-shrink-0 bg-zinc-800"
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
      >
        <i className={`${icon} text-xl`} style={{ color: iconColor }} />
      </motion.div>
      <div>
        <h2
          className="text-zinc-100 font-display font-bold tracking-wide"
          style={{ fontSize: '1.2rem', lineHeight: '1.3' }}
        >
          {title}
        </h2>
        <p className="text-zinc-500 text-xs tracking-widest mt-0.5 uppercase">{subtitle}</p>
      </div>
    </div>
  );

  // DISCOVERモードのコンテンツ
  const renderDiscoverContent = () => (
    <div className="w-full max-w-7xl mx-auto px-4 py-8 space-y-12">
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-12"
        >
          <FadeInSection>
            <div>
              <SectionHeading
                icon="ri-star-line"
                iconColor="#fb7185"
                title="PICKUP"
                subtitle="運営のおすすめ"
              />
              <PickupSection category={activeCategory} />
            </div>
          </FadeInSection>

          <FadeInSection delay={0.12}>
            <div>
              <SectionHeading
                icon="ri-map-pin-2-fill"
                iconColor="#22d3ee"
                title="地元での注目"
                subtitle="あなたのエリアのランキング"
              />
              <HotSection category={activeCategory} />
            </div>
          </FadeInSection>

          <FadeInSection delay={0.24}>
            <div>
              <SectionHeading
                icon="ri-sparkling-line"
                iconColor="#22d3ee"
                title="RECOMMEND"
                subtitle="あなたにおすすめ"
              />
              <RecommendSection category={activeCategory} />
            </div>
          </FadeInSection>
        </motion.div>
      </AnimatePresence>
    </div>
  );

  // SEARCHモードのコンテンツ
  const renderSearchContent = () => (
    <div className="w-full max-w-7xl mx-auto px-4 py-8">
      {/* CRASH バナー */}
      <FadeInSection>
        <motion.div
          whileHover={{ backgroundColor: 'rgba(39,39,42,0.7)' }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/resonance-card')}
          className="relative overflow-hidden rounded-2xl border border-zinc-700/50 cursor-pointer group mb-10 bg-zinc-800/40 backdrop-blur-md"
          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}
        >
          <div className="relative px-8 py-12 flex flex-col items-center text-center">
            <motion.div
              className="w-16 h-16 flex items-center justify-center rounded-2xl mb-5 bg-zinc-700"
              whileHover={{ rotate: 5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <i className="ri-shuffle-line text-4xl text-zinc-100" />
            </motion.div>
            <h2
              className="text-zinc-100 font-display font-bold mb-3"
              style={{ fontSize: '2rem', lineHeight: '1.2', letterSpacing: '0.05em' }}
            >
              知らない音に出会う
            </h2>
            <p className="text-zinc-400 text-base mb-6 max-w-2xl">
              ランダムにアーティストと出会い、新しい音楽体験を始めよう
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-zinc-700 border border-zinc-600">
                <i className="ri-fire-line text-xl text-amber-400" />
                <span className="text-zinc-100 text-base font-bold tracking-widest">CRASH</span>
              </div>
              <motion.div
                animate={{ x: [0, 6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <i className="ri-arrow-right-line text-2xl text-zinc-400" />
              </motion.div>
            </div>
            <p className="text-zinc-500 text-xs tracking-wider mt-4">
              クリックして未知の音楽世界へダイブ
            </p>
          </div>
        </motion.div>
      </FadeInSection>

      {/* カテゴリー別検索UI */}
      <div className="space-y-10">
        {(activeCategory === 'artists' || activeCategory === 'nodes') && (
          <>
            <FadeInSection delay={0.1}>
              <div>
                <SectionHeading
                  icon="ri-music-2-line"
                  iconColor="#fb7185"
                  title="ジャンルで探す"
                  subtitle={`好きな音楽ジャンルから${activeCategory === 'artists' ? 'アーティスト' : '店舗'}を発見`}
                />
                <ArtistSearch />
              </div>
            </FadeInSection>

            <FadeInSection delay={0.22}>
              <div>
                <SectionHeading
                  icon="ri-map-pin-line"
                  iconColor="#22d3ee"
                  title="マップ・エリアで探す"
                  subtitle={`地域から${activeCategory === 'artists' ? 'アーティスト' : '店舗'}を探索`}
                />
                <div className="mb-6 rounded-2xl border border-zinc-700/50 p-4 bg-zinc-800/40 backdrop-blur-md">
                  <JapanMap
                    prefectures={japanPrefectures}
                    hoveredPref={hoveredPref}
                    onHover={setHoveredPref}
                    onSelect={handlePrefectureSelect}
                  />
                </div>
                <PrefectureList
                  prefectures={japanPrefectures}
                  selectedRegion={selectedRegion}
                  onRegionChange={setSelectedRegion}
                  hoveredPref={hoveredPref}
                  onHover={setHoveredPref}
                  onSelect={handlePrefectureSelect}
                  counts={counts}
                />
              </div>
            </FadeInSection>
          </>
        )}

        {activeCategory === 'events' && (
          <FadeInSection delay={0.1}>
            <div>
              <SectionHeading
                icon="ri-calendar-event-line"
                iconColor="#22d3ee"
                title="イベントを探す"
                subtitle="カレンダー・地域・ジャンルからイベントを検索"
              />
              <EventsTab />
            </div>
          </FadeInSection>
        )}

        {activeCategory === 'market' && (
          <FadeInSection delay={0.1}>
            <div>
              <SectionHeading
                icon="ri-shopping-bag-3-line"
                iconColor="#a78bfa"
                title="グッズを探す"
                subtitle="アーティストの限定グッズをジャンルから検索"
              />
              <div className="flex flex-col items-center justify-center py-24 text-center rounded-2xl border border-zinc-700/50 bg-zinc-800/40 backdrop-blur-md">
                <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-zinc-700 mb-5">
                  <i className="ri-shopping-bag-3-line text-4xl text-zinc-300" />
                </div>
                <h3 className="text-zinc-100 font-display font-bold tracking-wider mb-3" style={{ fontSize: '1.2rem' }}>
                  COMING SOON
                </h3>
                <p className="text-zinc-500 text-sm max-w-md">
                  グッズのジャンル検索機能は現在開発中です
                </p>
                <div className="mt-5 flex items-center gap-2">
                  {[0, 200, 400].map((delay) => (
                    <div
                      key={delay}
                      className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-pulse"
                      style={{ animationDelay: `${delay}ms` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </FadeInSection>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: '#18181b' }}>
      {/* 背景オーブ */}
      <AbyssOrbs />

      {/* Sticky Header */}
      <div className="sticky top-0 z-40 backdrop-blur-md border-b border-zinc-700/50" style={{ background: 'rgba(24,24,27,0.92)' }}>
        {/* 上段：モード切替 */}
        <div className="max-w-7xl mx-auto px-4 pt-3 pb-2">
          <div className="flex items-center justify-center gap-2">
            {modes.map((mode) => {
              const isActive = exploreMode === mode.id;
              return (
                <motion.button
                  key={mode.id}
                  onClick={() => setExploreMode(mode.id)}
                  className={`relative px-4 py-1.5 rounded-full font-medium text-xs transition-all whitespace-nowrap cursor-pointer ${
                    isActive
                      ? 'bg-zinc-800 text-zinc-100 border border-brand-primary/40'
                      : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700'
                  }`}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <div className="flex items-center gap-1.5">
                    <i className={`${mode.icon} text-base`} />
                    <span>{mode.label}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* 下段：カテゴリタブ */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-around md:justify-center md:gap-8">
            {categories.map((category) => {
              const isActive = activeCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className="relative py-2.5 px-2 flex items-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${category.icon} text-base transition-colors ${isActive ? 'text-brand-primary' : 'text-zinc-500'}`} />
                  <span className={`text-xs font-medium transition-colors ${isActive ? 'text-brand-primary' : 'text-zinc-500'}`}>
                    {category.label}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="activeCategory"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="relative z-10">
        <AnimatePresence mode="wait">
          {exploreMode === 'discover' && (
            <motion.div
              key="discover"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              {renderDiscoverContent()}
            </motion.div>
          )}

          {exploreMode === 'search' && (
            <motion.div
              key="search"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              {renderSearchContent()}
            </motion.div>
          )}

          {exploreMode === 'favorites' && (
            <motion.div
              key="favorites"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <FavoritesTab activeCategory={activeCategory} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <BottomNav />
    </div>
  );
}